import FilterItem from '../FilterItem';
import deprecatedFilterItems from '../deprecatedFilterItems';
import FilterListStorage from './FilterListStorage';
import { STRATEGY_NOT, STRATEGY_AND, STRATEGY_OR } from 'Components/filters/config';
import { collectFeatureValue } from './featureValues';

class FilterList extends FilterItem {
  valuesStorage = null;
  // Declared upfront so Vue picks them up reactively: the instance is observed before init() resolves.
  valuesList = [];
  optionsLoading = false;
  /**
   * Shared between init() calls: every filters change re-inits all active items,
   * and some option endpoints take seconds to respond.
   * @type {Promise<Array>|null}
   */
  #optionsRequest = null;

  constructor(conf, subcategory) {
    super(conf, subcategory);
    this.valuesStorage = new FilterListStorage();
  }

  init = async () => {
    this.optionsLoading = true;
    try {
      if (this.property === 'tradeBrands.products.id' || this.property === 'manufacturedProducts.id') {
        this.valuesList = await this.#getListItems({ id: this.value[0] });
      } else {
        this.valuesList = await this.#loadOptionsOnce();
      }
    } finally {
      this.optionsLoading = false;
    }
    this.valuesStorage.setValuesList(this.valuesList);
    return this.initFilter();
  };

  #loadOptionsOnce() {
    if (!this.#optionsRequest) {
      this.#optionsRequest = this.#getListItems().catch((err) => {
        this.#optionsRequest = null;
        throw err;
      });
    }
    return this.#optionsRequest;
  }

  // eslint-disable-next-line no-unused-vars
  async apply(values, valuesMap, rawValue) {
    const hasNotEntries = Array.isArray(rawValue)
      ? rawValue.some((entry) => entry && typeof entry === 'object' && entry.not === true)
      : false;

    if (hasNotEntries || this.id.startsWith('not_feature')) {
      this.strategy = STRATEGY_NOT;
    } else if (Array.isArray(values[0])) {
      this.strategy = STRATEGY_OR;
    } else {
      this.strategy = STRATEGY_AND;
    }
    if (rawValue && rawValue.length) {
      const keys = Object.keys(rawValue[0]);
      if (rawValue[0].not === true && rawValue[0][keys[0]] == null) {
        this.na = true;
        return;
      } else {
        this.na = false;
      }
    }
    let items = [];
    if (this.options.getListItems) {
      items = await this.options.getListItems();
    } else {
      items = this.options.valuesList;
    }
    this.valuesStorage.setValuesList(items);
    const nextValue = this.strategy === STRATEGY_OR ? values[0] : values;
    this.value = this.valuesStorage.filterAvailableValues(nextValue);
  }

  resetValue = () => {
    this.value = [];
  };

  collectValues() {
    const data = {};
    if (this.isFeature) {
      data.__feature = this.#collectFeatures();
      return data;
    }
    if (this.isNA) {
      return this.naFilter;
    }
    if (
      this.property === 'platform' ||
      this.property === 'draft' ||
      this.property === 'source' ||
      this.property === 'type' ||
      this.property === 'status'
    ) {
      data[this.property] = this.value[0];
    } else {
      data[this.property] = this.value;
    }
    return data;
  }

  #collectFeatures() {
    const { featureID, value, strategy } = this;
    return collectFeatureValue({ featureID, value, strategy, isNA: this.isNA });
  }

  getListValues(showDeprecated = false) {
    if (!this.useMoreButton) {
      return this.valuesList;
    }
    return this.#manageDeprecatedListItems(showDeprecated);
  }

  removeValueFromList(id) {
    this.value = this.value.filter((item) => item != id);
  }

  #manageDeprecatedListItems(showDeprecated) {
    const actualValues = this.valuesList.filter((value) => !value.deprecated);
    if (!showDeprecated) {
      return actualValues;
    }
    const deprecatedValues = this.valuesList.filter((value) => value.deprecated);
    return [...actualValues, ...deprecatedValues];
  }

  #getListItems = async (...args) => {
    const { getListItems, valuesList, filterByCat, useMoreButton } = this.options;
    let list;
    if (typeof getListItems === 'function') {
      if (filterByCat) {
        list = await getListItems({ 'categories.id': this.subCat });
      } else {
        list = await getListItems(...args);
      }
    } else {
      list = valuesList || [];
    }
    if (filterByCat) {
      list = list.filter((el) => {
        if (!Array.isArray(el.categories)) {
          return true;
        }
        return el.categories.some((el) => {
          const id = typeof el === 'object' ? el.id : el;
          // noinspection EqualityComparisonWithCoercionJS
          return id == this.subCat;
        });
      });
    }
    if (useMoreButton) {
      const { deprecatedListPath } = this.options;
      const deprecatedList = deprecatedFilterItems[deprecatedListPath[0]][deprecatedListPath[1]];
      list = list.map((el) => ({
        ...el,
        deprecated: deprecatedList.items.includes(el[deprecatedList.value]),
      }));
      return list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  };
}

export default FilterList;
